/* global careld_ajax_obj */
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import './CareLabel.css';
import Icon from './Icon';
import axios from 'axios';
import html2canvas from 'html2canvas';

let cachedSpriteText = null;

const base64ToHex = (base64Str) => {
    if (!base64Str) return '';
    try {
        const commaIdx = base64Str.indexOf(',');
        const cleanStr = commaIdx !== -1 ? base64Str.substring(commaIdx + 1) : base64Str;
        const raw = window.atob(cleanStr);
        let result = '';
        for (let i = 0; i < raw.length; i++) {
            const hex = raw.charCodeAt(i).toString(16);
            result += (hex.length === 2 ? hex : '0' + hex);
        }
        return 'hex:' + result;
    } catch (e) {
        console.error('base64ToHex error:', e);
        return base64Str;
    }
};

const compressImage = (base64Str, maxWidth = 2400, maxHeight = 2400) => {
    if (!base64Str) return Promise.resolve('');
    if (base64Str.startsWith('data:image/svg+xml;base64,')) {
        return Promise.resolve(base64Str);
    }
    return new Promise((resolve) => {
        const img = new Image();
        img.src = base64Str;
        img.onload = () => {
            let width = img.width;
            let height = img.height;
            if (width <= maxWidth && height <= maxHeight) {
                return resolve(base64Str);
            }
            if (width > maxWidth || height > maxHeight) {
                if (width > height) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                } else {
                    width = Math.round((width * maxHeight) / height);
                    height = maxHeight;
                }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(base64Str);
    });
};

export default function CareLabel() {
    const t = (key, defaultText) => {
        if (window.careld_ajax_obj && window.careld_ajax_obj.translations && window.careld_ajax_obj.translations[key]) {
            return window.careld_ajax_obj.translations[key];
        }
        return defaultText;
    };

    const [text1, setText1] = useState("");
    const [text2, setText2] = useState("");
    const [text3, setText3] = useState("");
    const [font, setFont] = useState("Arial");

    const [fontCategory, setFontCategory] = useState("All");
    const [icons, setIcons] = useState([]); // Array to store up to 5 icons
    const [fontScrollPosition, setFontScrollPosition] = useState(0);
    // Memoized active quantities from settings
    const activeQuantities = useMemo(() => {
        const settings = window.careld_ajax_obj?.settings || {};
        if (Array.isArray(settings.active_quantities) && settings.active_quantities.length > 0) {
            return settings.active_quantities.map(Number).sort((a, b) => a - b);
        }
        return [250, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 6000, 7000, 8000, 9000, 10000];
    }, []);

    const [quantity, setQuantity] = useState(() => {
        const settings = window.careld_ajax_obj?.settings || {};
        const activeQs = Array.isArray(settings.active_quantities) ? settings.active_quantities.map(Number).sort((a, b) => a - b) : [];
        if (activeQs.length > 0) {
            return activeQs[0];
        }
        return 250;
    });
    const [loading, setLoading] = useState(false);
    const [labelWidth] = useState(70);
    const [labelHeight] = useState(30);
    const fontCategorySliderRef = useRef(null);
    const labelRef = useRef(null);
    const [fabricType, setFabricType] = useState("satin");

    // Mobile preview state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
    const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
    const [activeMobileTab, setActiveMobileTab] = useState('fabric');
    const [isPreviewZoomed, setIsPreviewZoomed] = useState(false);
    const isValid = !!(text1.trim() || text2.trim() || text3.trim());

    const fabricTypes = useMemo(() => [
        { value: 'satin', title: t('satin', 'Satin'), desc: t('smooth_finish', 'Smooth finish') },
        { value: 'polyester-tafetta', title: t('polyester_tafetta', 'Polyester Tafetta'), desc: t('durable_texture', 'Durable texture') }
    ], []);

    // Character limits for Text1, Text2, and Text3
    const maxCharsText1 = 20; // Adjusted based on label width
    const maxCharsText2 = 85;
    const maxCharsText3 = 200; // New character limit for Text3

    // Maximum lines for Text1, Text2, and Text3
    const maxLinesText1 = 2;
    const maxLinesText2 = 4;
    const maxLinesText3 = 8;

    // %7 büyütme faktörü
    const scaleFactor = 2.3;

    // Mobile detection
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1200);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Close mobile preview when screen size changes
    useEffect(() => {
        if (!isMobile && isMobilePreviewOpen) {
            setIsMobilePreviewOpen(false);
        }
    }, [isMobile, isMobilePreviewOpen]);

    const formatText = (text, maxChars, maxLines) => {
        // Metin boşsa direkt dön
        if (!text) return text;

        // Kelimelerle çalışalım
        const words = text.split(' ');
        const lines = [''];
        let currentLine = 0;
        let currentLength = 0;

        for (let word of words) {
            // Kelime + boşluk eklendiğinde satır karakter limitini aşıyor mu?
            if (currentLength + word.length + 1 > maxChars / maxLines) {
                // Eğer maksimum satır sayısına ulaşılmadıysa yeni satır başlat
                if (currentLine + 1 < maxLines) {
                    currentLine++;
                    lines[currentLine] = word;
                    currentLength = word.length;
                }
            } else {
                // Mevcut satıra kelimeyi ekle
                if (lines[currentLine].length > 0) {
                    lines[currentLine] += ' ';
                }
                lines[currentLine] += word;
                currentLength += word.length + 1;
            }
        }

        // maxLines kadar satırı birleştir
        return lines.slice(0, maxLines).join('\n');
    };

    const handleTextChange = (setter, maxChars, maxLines) => (e) => {
        const value = e.target.value;

        // Satırları al
        const lines = value.split('\n');

        // Karakter limitini kontrol et
        if (value.length > maxChars) {
            return;
        }

        // Satır sayısını kontrol et
        if (lines.length > maxLines) {
            // Sadece izin verilen satır sayısı kadar al
            const limitedText = lines.slice(0, maxLines).join('\n');
            setter(limitedText);
            return;
        }

        // Tüm kontroller geçildiyse değeri set et
        setter(value);
    };

    const fontCategories = useMemo(() => {
        const baseCategories = {
            "Sans Serif": [
                "Roboto", "Open Sans", "Montserrat", "Poppins", "Inter",
                "Lato", "Rubik", "Work Sans", "Nunito", "Source Sans 3",
                "Barlow", "Public Sans"
            ],
            "Serif": [
                "Playfair Display", "Merriweather", "Lora", "Crimson Pro",
                "Libre Baskerville", "EB Garamond", "Zilla Slab", "Cardo",
                "Bitter", "PT Serif", "Vollkorn", "Source Serif 4"
            ],
            "Handwriting": [
                "Caveat", "Dancing Script", "Patrick Hand", "Indie Flower",
                "Kalam", "Shadows Into Light", "Amatic SC", "Architects Daughter",
                "Gochi Hand", "Annie Use Your Telescope", "Coming Soon",
                "Nothing You Could Do"
            ],
            "Decorative": [
                "Bebas Neue", "Oswald", "Lobster", "Abril Fatface", "Bangers",
                "Alfa Slab One", "Shrikhand", "Fredericka the Great",
                "Black Han Sans", "Big Shoulders", "Passion One", "Rammetto One"
            ],
            "Modern": [
                "Futura", "Manrope", "Overpass", "Jost", "Sora", "Outfit",
                "Chivo", "Karla", "Archivo", "DM Sans", "Exo 2", "Cabin"
            ],
            "Vintage": [
                "Special Elite", "Old Standard TT", "Cinzel", "IM Fell English",
                "UnifrakturMaguntia", "Spectral", "Rye", "Almendra", "Pirata One",
                "Metal Mania", "Germania One", "Caesar Dressing"
            ],
            "Script": [
                "Great Vibes", "Sacramento", "Alex Brush", "Kaushan Script",
                "Parisienne", "Yellowtail", "Tangerine", "Clicker Script",
                "Allura", "Bad Script", "Lovers Quarrel", "Cedarville Cursive"
            ],
            "Monospace": [
                "Fira Code", "IBM Plex Mono", "Space Mono", "Inconsolata",
                "Roboto Mono", "Source Code Pro", "JetBrains Mono", "Courier Prime",
                "Overpass Mono", "Ubuntu Mono", "VT323", "Share Tech Mono"
            ],
        };

        const allFonts = [...new Set(Object.values(baseCategories).flat())];
        return {
            "All": allFonts,
            ...baseCategories
        };
    }, []);

    // Sabit ikon listesi
    const iconNames = useMemo(() => {
        const washingIcons = [
            // Yıkama Ana Kategorisi
            "machine-wash",
            "machine-wash-cold-gentle",
            "machine-wash-cold-gentle-2",
            "machine-wash-cold-permanent-press",
            "machine-wash-cold-permanent-press-30c",
            "machine-wash-warm-gentle",
            "machine-wash-warm-gentle-2",
            "machine-wash-warm-permanent-press-2",
            "machine-wash-warm-permanent-press-40c",
            "machine-wash-hot-gentle",
            "machine-wash-hot-gentle-2",
            "machine-wash-hot-gentle-3",
            "machine-wash-hot-gentle-5",
            "machine-wash-hot-gentle-50c",
            "machine-wash-hot-gentle-60c",
            "machine-wash-hot-gentle-70c",
            "machine-wash-hot-permanent-press",
            "machine-wash-hot-permanent-press-2",
            "machine-wash-hot-permanent-press-3",
            "machine-wash-hot-permanent-press-50c",
            "machine-wash-hot-permanent-press-60c",
            "machine-wash-hot-permanent-press-70c",
            "machine-wash-hot-permanent-press-95c",
            "machine-wash-hot-permanent-press4",
            "machine-wash-permanent-press-5",
            "machine-wash-permanent-press",
            "machine-wash-delicate",
            "hand-wash",
            "hand-wash-cold",
            "hand-wash-warm",
        ];

        const temperatureIcons = [
            // Sıcaklık Göstergeleri
            "water-30c", "water-30c-2",
            "water-40c", "water-40c-2",
            "water-50c", "water-50c-2",
            "water-60c", "water-60c-2",
            "water-70c", "water-70c-2",
            "water-95c", "water-95c-2",
        ];

        const dryingIcons = [
            // Kurutma Kategorisi
            "tumble-dry-gentle",
            "tumble-dry-gentle-high-heat",
            "tumble-dry-gentle-medium-heat",
            "tumble-dry-gentle-low-heat",
            "tumble-dry-permanent-press",
            "tumble-dry-permanent-press-high-heat",
            "tumble-dry-permanent-press-medium-heat",
            "tumble-dry-permanent-press-no-heat",
            "tumble-dry-premanent-press-low-heat",
            "drip-dry",
            "drip-dry-in-shade",
            "dry-flat",
            "hang-to-dry",
            "shade-dry",
            "dry",
        ];

        const heatLevelIcons = [
            // Isı Seviyesi
            "high-heat",
            "medium-heat",
            "low-heat",
            "low-heat-1",
            "no-heat",
        ];

        const ironingIcons = [
            // Ütüleme Kategorisi
            "iron",
            "iron-high-temperature",
            "iron-medium-temperature",
            "iron-low-temperature",
            "iron-high-no-steam",
            "iron-medium-no-steam",
            "iron-no-steam",
            "no-steam",
        ];

        const bleachIcons = [
            // Ağartma Kategorisi
            "bleach",
            "chlorine-bleach",
            "non-chlorine-bleach",
            "non-chlorine-bleach-2",
        ];

        const dryCleaningIcons = [
            // Kuru Temizleme
            "dry-clean",
            "dry-clean-any-solvent-reduced-misture",
            "dry-clean-any-solvent-short-cycle",
            "any-solvent",
            "any-solvent-except-trichloroethylene",
            "petroleum-solvent-only",
            "professional-dry-cleaning-in-hydrocarbons",
            "professional-wet-cleaning",
            "wet-cleaning",
            "reduced-moisture",
            "short-cycle",
        ];

        const prohibitedIcons = [
            // Yasaklı/Yapılmayacaklar
            "do-not-wash",
            "do-not-bleach",
            "do-not-dry",
            "do-not-iron",
            "do-not-dry-clean",
            "do-not-tumble-dry",
            "do-not-wet-clean",
        ];

        const miscIcons = [
            // Diğer
            "delicate",
            "permanent-press",
            "wring",
            "not-professional-wet-cleaning",
        ];

        // Tüm kategorileri birleştir
        return [
            ...washingIcons,
            ...temperatureIcons,
            ...dryingIcons,
            ...heatLevelIcons,
            ...ironingIcons,
            ...bleachIcons,
            ...dryCleaningIcons,
            ...prohibitedIcons,
            ...miscIcons
        ];
    }, []);

    const handleFontScroll = useCallback((e) => {
        setFontScrollPosition(e.currentTarget.scrollLeft);
    }, []);

    const scrollFontCategories = useCallback((direction) => {
        const container = fontCategorySliderRef.current;
        if (container) {
            const scrollAmount = 200;
            const currentScroll = container.scrollLeft;
            const newPosition = direction === 'left'
                ? currentScroll - scrollAmount
                : currentScroll + scrollAmount;

            container.scrollTo({
                left: newPosition,
                behavior: 'smooth'
            });

            setFontScrollPosition(newPosition);
        }
    }, []);

    const addIcon = useCallback((iconName) => {
        // İkon zaten seçilmişse ekleme yapma
        if (icons.some(icon => icon.name === iconName)) {
            return;
        }

        if (icons.length >= 5) {
            return;
        }
        setIcons([...icons, { name: iconName }]);
    }, [icons]);

    const removeIcon = useCallback((iconName) => {
        setIcons(icons.filter(icon => icon.name !== iconName));
    }, [icons]);

    const calculatePrice = useCallback(() => {
        // Ayarları kontrol et ve varsayılan değerleri belirle
        const settings = window.careld_ajax_obj?.settings || {};

        // Temel fiyatları al veya varsayılan değerleri kullan
        const satinFee = parseFloat(settings.fabric_satin_fee) || 0.10;
        const polyesterFee = parseFloat(settings.fabric_polyester_tafetta_fee) || 0.15;

        // Base price hesapla
        let basePrice = fabricType === 'polyester-tafetta' ? polyesterFee : satinFee;

        // Genişliğe göre çarpan uygula
        if (labelWidth > 100 && labelWidth <= 140) {
            basePrice *= 2;
        } else if (labelWidth > 140) {
            basePrice *= 3;
        }

        // İndirim oranını hesapla
        const quantities = [...activeQuantities];
        let discountRate = 0;
        let isMarkup = false;

        const parseDiscountOrMarkup = (value) => {
            if (value === undefined || value === null) return { rate: 0, isMarkup: false };
            const strVal = String(value).trim();
            const markup = strVal.startsWith('+');
            const numStr = strVal.replace(/[^\d.]/g, '');
            const rate = parseFloat(numStr);
            return {
                rate: isNaN(rate) ? 0 : rate,
                isMarkup: markup
            };
        };

        // En yüksek uygun indirim oranını bul
        for (const qty of quantities.sort((a, b) => b - a)) {
            if (quantity >= qty) {
                const discountKey = `quantity_${qty}_discount`;
                const parsed = parseDiscountOrMarkup(settings[discountKey]);
                discountRate = parsed.rate;
                isMarkup = parsed.isMarkup;
                break;
            }
        }

        // Final fiyatı hesapla
        const finalPrice = isMarkup 
            ? basePrice * ((100 + discountRate) / 100) 
            : basePrice * ((100 - discountRate) / 100);

        return {
            total: (quantity * finalPrice).toFixed(2),
            perPiece: finalPrice.toFixed(2),
            discount: discountRate,
            isMarkup: isMarkup
        };
    }, [labelWidth, quantity, fabricType]);

    const renderLabelContent = useCallback(() => {
        const fontSizeText2 = 16;
        const fontSizeText3 = 16;

        return (
            <div className="label-container">
                <div className={`label-content-wrapper ${fabricType}`}>
                    <div className="label-text-display">
                        {/* Text1 */}
                        <div className="text-section top">
                            <span
                                className="text-display"
                                style={{
                                    fontFamily: font,
                                    fontSize: `28px`,
                                    whiteSpace: 'pre',
                                    color: text1 ? '#000000' : '#9CA3AF',
                                    width: '100%',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'clip',
                                    lineHeight: '1.1',
                                    minHeight: `${28 * 1.1 * maxLinesText1}px`, // 28px font size için yükseklik
                                    maxHeight: `${28 * 1.1 * maxLinesText1}px`, // 28px font size için yükseklik
                                    padding: '2px 0'
                                }}
                            >
                                {text1 || t("enter_text_1", "Enter Text 1")}
                            </span>
                        </div>

                        {/* Text2 - her zaman göster */}
                        <div className="text-section top-adjacent">
                            <span
                                className="text-display"
                                style={{
                                    fontFamily: 'Arial',
                                    fontSize: `${fontSizeText2}px`,
                                    whiteSpace: 'pre',
                                    color: text2 ? '#000000' : '#9CA3AF',
                                    width: '100%',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'clip',
                                    lineHeight: '1.1', // line-height azaltıldı
                                    minHeight: `${16 * 1.3 * maxLinesText2}px`, // Yükseklik güncellendi
                                    maxHeight: `${16 * 1.3 * maxLinesText2}px`, // Yükseklik güncellendi
                                    padding: '2px 0' // Üst ve alt padding eklendi
                                }}
                            >
                                {text2 || t("enter_text_2", "Enter Text 2")}
                            </span>
                        </div>

                        {/* İkonlar */}
                        <div className="middle-content">
                            {icons.length > 0 && (
                                <div className="icons-section">
                                    {icons.map((iconObj, index) => (
                                        <span
                                            key={`icon-${index}`}
                                            className="icon"
                                        >
                                            <Icon name={iconObj.name} color="#000000" size={40} />
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Text3 - her zaman göster */}
                        <div className="bottom-content">
                            <div className="text-section bottom">
                                <span
                                    className="text-display"
                                    style={{
                                        fontFamily: 'Arial',
                                        fontSize: `${fontSizeText3}px`,
                                        whiteSpace: 'pre',
                                        color: text3 ? '#000000' : '#9CA3AF',
                                        width: '100%',
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'clip',
                                        lineHeight: '1.3', // line-height azaltıldı
                                        minHeight: `${16 * 1.3 * maxLinesText3}px`, // Yükseklik güncellendi
                                        maxHeight: `${16 * 1.3 * maxLinesText3}px`, // Yükseklik güncellendi
                                        padding: '2px 0' // Üst ve alt padding eklendi
                                    }}
                                >
                                    {text3 || t("enter_text_3", "Enter Text 3")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile Preview Tab */}
                {isMobile && (
                    <button
                        className={`mobile-preview-tab ${isMobilePreviewOpen ? 'close-preview' : ''}`}
                        onClick={() => setIsMobilePreviewOpen(!isMobilePreviewOpen)}
                    >
                        {isMobilePreviewOpen ? t('close_preview', 'Close Preview') : t('show_preview', 'Show Preview')}
                    </button>
                )}

                {/* Mobile Preview Modal */}
                {isMobile && isMobilePreviewOpen && (
                    <div
                        className="mobile-preview-modal"
                        onClick={(e) => {
                            if (e.target.classList.contains('mobile-preview-modal')) {
                                setIsMobilePreviewOpen(false);
                            }
                        }}
                    >
                        <div onClick={(e) => e.stopPropagation()}>
                            <div className="preview-header">
                                <div className="preview-info">
                                    <span className="preview-title">{t("care_label_preview", "Care Label Preview")}</span>
                                    <div className="dimension-info">
                                        <span className="actual-size">{`${labelHeight}x${labelWidth}mm`}</span>
                                    </div>
                                </div>
                                <span className="preview-subtitle">
                                    {fabricType === 'satin' ? t('satin', 'Satin') : t('polyester_tafetta', 'Polyester Tafetta')}
                                </span>
                            </div>
                            <div className="canvas-frame" onClick={() => setIsPreviewZoomed(true)} style={{ cursor: 'zoom-in' }}>
                                <div
                                    ref={labelRef}
                                    className="label-preview"
                                    data-width={`${(labelHeight * 3) * scaleFactor}px`}
                                    data-height={`${(labelWidth * 3) * scaleFactor}px`}
                                    style={{
                                        width: `${(labelHeight * 3) * scaleFactor}px`,
                                        height: `${(labelWidth * 3) * scaleFactor}px`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {/* Size Indicator - değerlerin yerlerini değiştirdik */}
                                    <div className="size-indicator">
                                        <div className="width-indicator">{`${labelHeight}mm`}</div>
                                        <div className="height-indicator">{`${labelWidth}mm`}</div>
                                        <div className="dimension-line width-line" />
                                        <div className="dimension-line height-line" />
                                    </div>
                                    {renderLabelContent()}
                                </div>
                            </div>
                            <div className="order-section">
                                <div className="quantity-selector">
                                    <label htmlFor="quantity">{t("quantity", "Quantity:")}</label>
                                    <select
                                        id="quantity"
                                        value={quantity}
                                        onChange={(e) => setQuantity(Number(e.target.value))}
                                        disabled={loading}
                                    >
                                        {activeQuantities.map(q => (
                                            <option key={q} value={q}>{q} {t("pieces", "pieces")}</option>
                                        ))}
                                    </select>
                                </div>
                                {isValid && (
                                    <div className="price-info">
                                        <div className="total-price">
                                            <span>{t("total_price", "Total Price")}</span>
                                            <div className="price-with-discount">
                                                <span>{window.careld_ajax_obj?.currency_symbol || '$'} {prices.total}</span>
                                                {prices.discount > 0 && !prices.isMarkup && (
                                                    <span className="discount-info">
                                                        -{prices.discount}% {t("off", "off")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="price-per-piece">
                                            {window.careld_ajax_obj?.currency_symbol || '$'} {prices.perPiece} {t("per_piece", "per piece")}
                                        </div>
                                    </div>
                                )}
                                <button
                                    className="add-to-cart-button"
                                    onClick={addToCart}
                                    disabled={loading || !isValid}
                                >
                                    {loading ? t('adding_to_cart', 'Adding to Cart...') : t('add_to_cart', 'Add to Cart')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }, [text1, text2, text3, font, icons, fabricType]);

    const captureLabel = useCallback(async () => {
        try {
            // Font yüklemesini bekle
            if (document.fonts && document.fonts.ready) {
                await document.fonts.ready;
            }

            // SVG sprite'ı yükle
            const spriteUrl = `${window.careld_ajax_obj.plugin_url}assets/sprite.svg`;
            let tempContainer = null;

            try {
                let svgText = cachedSpriteText;
                if (!svgText) {
                    const response = await fetch(spriteUrl);
                    svgText = await response.text();
                    cachedSpriteText = svgText;
                }

                tempContainer = document.createElement('div');
                tempContainer.style.display = 'none';
                tempContainer.innerHTML = svgText;
                document.body.appendChild(tempContainer);
            } catch (error) {
                console.error('SVG sprite yüklenemedi:', error);
            }

            // Ana container
            const container = document.createElement('div');
            container.style.cssText = `
            position: absolute;
            left: -9999px;
            top: -9999px;
            width: ${labelHeight * 3 * scaleFactor}px;
            height: ${labelWidth * 3 * scaleFactor}px;
            background-color: #FFFFFF;
            display: flex;
            align-items: center;
            justify-content: center;
        `;

            // Label container
            const labelContainer = document.createElement('div');
            labelContainer.className = 'label-container';
            labelContainer.style.cssText = `
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        `;

            // Content wrapper
            const contentWrapper = document.createElement('div');
            contentWrapper.className = `label-content-wrapper ${fabricType}`;
            contentWrapper.style.cssText = `
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            padding: 25px 10px;
            box-sizing: border-box;
        `;

            // Text display container
            const textDisplay = document.createElement('div');
            textDisplay.className = 'label-text-display';
            textDisplay.style.cssText = `
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
        `;

            // Top content wrapper for text1 and text2
            const topContent = document.createElement('div');
            topContent.style.cssText = `
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
        `;

            // Text1 Section
            const text1Section = document.createElement('div');
            text1Section.className = 'text-section top';
            text1Section.style.cssText = `
            width: 100%;
            text-align: center;
        `;
            const text1Span = document.createElement('span');
            text1Span.className = 'text-display';
            text1Span.style.cssText = `
    font-family: ${font};
    font-size: 28px;
    white-space: pre;
    color: ${text1 ? '#000000' : '#9CA3AF'};
    text-align: center;
    width: 100%;
    display: block;
    overflow: hidden;
    text-overflow: clip;
    line-height: 1.1;
    min-height: ${28 * 1.1 * maxLinesText1}px;
    max-height: ${28 * 1.1 * maxLinesText1}px;
    padding: 2px 0;
`;
            text1Span.textContent = text1 || '';
            text1Section.appendChild(text1Span);

            // Text2 Section
            const text2Section = document.createElement('div');
            text2Section.className = 'text-section top-adjacent';
            text2Section.style.cssText = `
            width: 100%;
            text-align: center;
        `;
            const text2Span = document.createElement('span');
            text2Span.className = 'text-display';
            text2Span.style.cssText = `
    font-family: Arial;
    font-size: 16px;
    white-space: pre;
    color: ${text2 ? '#000000' : '#9CA3AF'};
    text-align: center;
    width: 100%;
    display: block;
    overflow: hidden;
    text-overflow: clip;
    line-height: 1.1;
    min-height: ${16 * 1.3 * maxLinesText2}px;
    max-height: ${16 * 1.3 * maxLinesText2}px;
    padding: 2px 0;
`;
            text2Span.textContent = text2 || '';
            text2Section.appendChild(text2Span);

            // Top content birleştirme
            topContent.appendChild(text1Section);
            topContent.appendChild(text2Section);

            // Middle content for icons
            const middleContent = document.createElement('div');
            middleContent.className = 'middle-content';
            middleContent.style.cssText = `
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 2;
            max-width: 200px;
        `;

            if (icons.length > 0 && tempContainer) {
                const iconContainer = document.createElement('div');
                iconContainer.className = 'icons-section';
                iconContainer.style.cssText = `
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 6px;
                width: 100%;
            `;

                const symbols = tempContainer.querySelectorAll('symbol');
                const symbolsMap = new Map();
                symbols.forEach(symbol => {
                    symbolsMap.set(symbol.id, {
                        content: symbol.innerHTML,
                        viewBox: symbol.getAttribute('viewBox') || '0 0 24 24'
                    });
                });

                for (const iconObj of icons) {
                    const iconWrapper = document.createElement('span');
                    iconWrapper.className = 'icon';
                    iconWrapper.style.cssText = `
                    width: 35px;
                    height: 35px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                `;

                    if (symbolsMap.has(iconObj.name)) {
                        const symbolData = symbolsMap.get(iconObj.name);
                        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                        svg.setAttribute('width', '35');
                        svg.setAttribute('height', '35');
                        svg.setAttribute('viewBox', symbolData.viewBox);
                        svg.style.cssText = `
                        width: 35px;
                        height: 35px;
                        fill: #000000;
                    `;
                        svg.innerHTML = symbolData.content;

                        svg.querySelectorAll('path, circle, rect').forEach(el => {
                            el.setAttribute('fill', '#000000');
                        });

                        iconWrapper.appendChild(svg);
                    }
                    iconContainer.appendChild(iconWrapper);
                }

                middleContent.appendChild(iconContainer);
            }

            // Bottom content for Text3
            const bottomContent = document.createElement('div');
            bottomContent.className = 'bottom-content';
            bottomContent.style.cssText = `
            width: 100%;
            margin-top: auto;
            padding-bottom: 20px;
        `;

            const text3Section = document.createElement('div');
            text3Section.className = 'text-section bottom';
            text3Section.style.cssText = `
            width: 100%;
            text-align: center;
        `;
            const text3Span = document.createElement('span');
            text3Span.className = 'text-display';
            text3Span.style.cssText = `
    font-family: Arial;
    font-size: 16px;
    white-space: pre;
    color: ${text3 ? '#000000' : '#9CA3AF'};
    text-align: center;
    width: 100%;
    display: block;
    overflow: hidden;
    text-overflow: clip;
    line-height: 1.3;
    min-height: ${16 * 1.3 * maxLinesText3}px;
    max-height: ${16 * 1.3 * maxLinesText3}px;
    padding: 2px 0;
`;
            text3Span.textContent = text3 || '';
            text3Section.appendChild(text3Span);
            bottomContent.appendChild(text3Section);

            // DOM yapısını birleştir
            textDisplay.appendChild(topContent);
            textDisplay.appendChild(middleContent);
            textDisplay.appendChild(bottomContent);
            contentWrapper.appendChild(textDisplay);
            labelContainer.appendChild(contentWrapper);
            container.appendChild(labelContainer);

            document.body.appendChild(container);

            // DOM'un stabilize olmasını bekle
            await new Promise(resolve => setTimeout(resolve, 100));

            // html2canvas ile yakala
            const canvas = await html2canvas(container, {
                scale: 5,
                devicePixelRatio: 1,
                backgroundColor: '#FFFFFF',
                width: labelHeight * 3 * scaleFactor,
                height: labelWidth * 3 * scaleFactor,
                imageRendering: 'auto',
                allowTaint: true,
                useCORS: true,
                logging: false,
                onclone: (doc, element) => {
                    element.querySelectorAll('.icon svg').forEach(svg => {
                        svg.setAttribute('width', '35');
                        svg.setAttribute('height', '35');
                        svg.style.width = '35px';
                        svg.style.height = '35px';
                        svg.style.fill = '#000000';

                        svg.querySelectorAll('path, circle, rect').forEach(el => {
                            el.setAttribute('fill', '#000000');
                        });
                    });
                }
            });

            // Temizlik
            document.body.removeChild(container);
            if (tempContainer) {
                document.body.removeChild(tempContainer);
            }

            return canvas.toDataURL('image/png');

        } catch (error) {
            console.error('Error capturing label:', error);
            return null;
        }
    }, [text1, text2, text3, font, icons, fabricType, labelWidth, labelHeight, scaleFactor]);

    const addToCart = useCallback(async () => {
        if (loading || (!text1.trim() && !text2.trim() && !text3.trim())) {
            alert('Please add at least one line of label text');
            return;
        }

        setLoading(true);
        await new Promise(resolve => setTimeout(resolve, 100));

        try {
            const labelImage = await captureLabel();
            if (!labelImage) {
                throw new Error('Failed to create label image');
            }

            const compressedImage = await compressImage(labelImage, 2400, 2400);

            const designData = {
                text1,
                text2,
                text3,
                font,
                icons,
                fabricType,
                labelWidth,
                labelHeight,
                imageData: base64ToHex(compressedImage),
                created_at: new Date().toISOString()
            };

            const data = {
                action: 'add_to_cart_care_label',
                security: window.careld_ajax_obj.nonce,
                label_design: JSON.stringify(designData),
                quantity: quantity.toString()
            };

            // AJAX URL'i kontrol et
            console.log('AJAX URL:', window.careld_ajax_obj.ajax_url);

            const response = await axios({
                method: 'POST',
                url: window.careld_ajax_obj.rest_add_to_cart || window.careld_ajax_obj.ajax_url,
                data: new URLSearchParams(data),
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });
            console.log('Server Response:', response);

            if (response.data.success) {
                window.location.href = response.data.data.cart_url;
            } else {
                throw new Error(response.data.data.message || 'Error adding to cart');
            }
        } catch (error) {
            console.error('Add to cart error:', error);
            alert('Error adding to cart: ' + (error.response?.data?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    }, [loading, text1, text2, text3, font, icons, fabricType, labelWidth, labelHeight, quantity, captureLabel]);

    const prices = useMemo(() => calculatePrice(), [calculatePrice]);

    return (
        <div className="label-designer-wrapper">
            <div className="orientation-warning-overlay">
                <span>{t('please_rotate_your_device_to_portrait', 'Please rotate your device to portrait')}</span>
            </div>
            <div className="designer-container">
                {/* Mobile Category Tabs */}
                <div className="mobile-category-tabs">
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'fabric' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('fabric'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('fabric', 'Fabric')}
                    </button>
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'text_settings' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('text_settings'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('text_settings', 'Text Settings')}
                    </button>
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'care_symbols' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('care_symbols'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('care_symbols', 'Care Symbols')}
                    </button>
                </div>

                <div className="designer-controls" data-active-mobile-tab={activeMobileTab}>
                    {/* Fabric Type Section - Moved to Top */}
                    <div className="control-group tab-fabric">
                        <div className="group-title">
                            {t("fabric_type_colon", "Fabric Type").replace(":", "")}
                            <span className="help-icon">?
                                <span className="tooltip">
                                    {t("tooltip_fabric", "Choose a fabric texture to customize your label appearance.")}
                                </span>
                            </span>
                        </div>
                        <div className="fabric-type-buttons">
                            {fabricTypes.map(fabric => (
                                <button
                                    key={fabric.value}
                                    className={`fabric-type-button ${fabric.value} ${fabricType === fabric.value ? 'selected' : ''}`}
                                    onClick={() => setFabricType(fabric.value)}
                                >
                                    <div className="fabric-icon"></div>
                                    <div className="fabric-info">
                                        <span className="fabric-title">{fabric.title}</span>
                                        <span className="fabric-desc">{fabric.desc}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Text Input Section */}
                    <div className="control-group tab-text_settings">
                        <div className="text-input-section">
                            <div className="group-title">
                                {t("text_settings", "Text Settings")}
                                <span className="help-icon">?
                                    <span className="tooltip">
                                        {t("tooltip_text", "Enter the text for your label here.")}
                                    </span>
                                </span>
                            </div>
                            <div className="text-input-group">
                                <label htmlFor="text1">{t("text_1_colon", "Text 1").replace(":", "")}</label>
                                <textarea
                                    id="text1"
                                    placeholder={t("enter_text_1", "Enter Text 1")}
                                    value={text1}
                                    onChange={handleTextChange(setText1, maxCharsText1, maxLinesText1)}
                                    rows={2}
                                />
                            </div>
                            <div className="text-input-group">
                                <label htmlFor="text2">{t("text_2_colon", "Text 2").replace(":", "")}</label>
                                <textarea
                                    id="text2"
                                    placeholder={t("enter_text_2", "Enter Text 2")}
                                    value={text2}
                                    onChange={handleTextChange(setText2, maxCharsText2, maxLinesText2)}
                                    rows={3}
                                />
                            </div>
                            <div className="text-input-group">
                                <label htmlFor="text3">{t("text_3_colon", "Text 3").replace(":", "")}</label>
                                <textarea
                                    id="text3"
                                    placeholder={t("enter_text_3", "Enter Text 3")}
                                    value={text3}
                                    onChange={handleTextChange(setText3, maxCharsText3, maxLinesText3)}
                                    rows={4}
                                />
                            </div>
                        </div>

                        {/* Font Selection Section - Merged directly into Text Settings */}
                        <div className="font-selection-merged" style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                            <div className="group-title">
                                {t("font_colon", "Font Style").replace(":", "")}
                                <span className="help-icon">?
                                    <span className="tooltip">
                                        {t("tooltip_font", "Choose from a variety of fonts to customize your label's appearance.")}
                                    </span>
                                </span>
                            </div>
                            <div className="font-category-selector">
                                <button
                                    className="scroll-button left"
                                    type="button"
                                    onClick={() => scrollFontCategories('left')}
                                    disabled={fontScrollPosition <= 5}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        strokeWidth="2">
                                        <path d="M15 18l-6-6 6-6" />
                                    </svg>
                                </button>
                                <div className="font-category-list" ref={fontCategorySliderRef} onScroll={handleFontScroll}>
                                    {Object.entries(fontCategories).map(([category, fonts]) => (
                                        <button
                                            key={category}
                                            type="button"
                                            className={`font-category-button ${fontCategory === category ? 'active' : ''}`}
                                            onClick={() => setFontCategory(category)}
                                        >
                                            <span
                                                className="font-preview-text"
                                                style={{ fontFamily: fonts[0] }}
                                            >
                                                Aa
                                            </span>
                                            <span className="font-category-name">{t(category.toLowerCase().replace(/\s+/g, '_'), category)}</span>
                                        </button>
                                    ))}
                                </div>
                                <button
                                    className="scroll-button right"
                                    type="button"
                                    onClick={() => scrollFontCategories('right')}
                                    disabled={fontScrollPosition >= (fontCategorySliderRef.current?.scrollWidth - fontCategorySliderRef.current?.clientWidth - 5)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        strokeWidth="2">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </button>
                            </div>
                            <div className="font-options-grid">
                                {fontCategories[fontCategory].map(fontName => (
                                    <button
                                        key={fontName}
                                        type="button"
                                        className={`font-option ${font === fontName ? 'selected' : ''}`}
                                        style={{ fontFamily: fontName }}
                                        onClick={() => setFont(fontName)}
                                    >
                                        <span className="font-preview">Aa</span>
                                        <span className="font-name">{fontName}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Icon Selection Section */}
                    <div className="control-group tab-care_symbols">
                        <div className="group-title">
                            {t("icons_title", "Icons")}
                            <span className="help-icon">?
                                <span className="tooltip">
                                    {t("tooltip_icons", "Add up to 5 icons to enhance your care label design.")}
                                </span>
                            </span>
                        </div>
                        <div className="control-selected-icons">
                            {icons.map((iconObj, index) => (
                                <div key={index} className="control-selected-icon">
                                    <Icon name={iconObj.name} color="#000000" size={24} />
                                    <button
                                        className="remove-icon"
                                        onClick={() => removeIcon(iconObj.name)}
                                        aria-label="Remove icon"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="icon-options-grid">
                            {iconNames.map((iconName) => {
                                const isSelected = icons.some(icon => icon.name === iconName);
                                return (
                                    <div key={iconName} className="icon-option-wrapper">
                                        <button
                                            className={`icon-option ${isSelected ? 'selected' : ''}`}
                                            onClick={() => addIcon(iconName)}
                                            disabled={isSelected}
                                        >
                                            <span className="icon-preview">
                                                <Icon name={iconName} color="#000000" size={24} />
                                            </span>
                                        </button>
                                        {isSelected && (
                                            <button className="control-remove-icon"
                                                onClick={() => removeIcon(iconName)}>
                                                <svg viewBox="0 0 24 24" width="12" height="12">
                                                    <path d="M6 6l12 12M18 6L6 18" stroke="white" strokeWidth="2" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Label Preview */}
                <div className="designer-canvas">
                    <div className="preview-header">
                        <div className="preview-info">
                            <span className="preview-title">{t("care_label_preview", "Care Label Preview")}</span>
                            <div className="dimension-info">
                                <span className="actual-size">{`${labelHeight}x${labelWidth}mm`}</span>
                            </div>
                        </div>
                        <span className="preview-subtitle">
                            {fabricType === 'satin' ? t('satin', 'Satin') : t('polyester_tafetta', 'Polyester Tafetta')}
                        </span>
                    </div>
                    <div className="canvas-frame" onClick={() => setIsPreviewZoomed(true)} style={{ cursor: 'zoom-in' }}>
                        <div
                            ref={labelRef}
                            className="label-preview"
                            data-width={`${(labelHeight * 3) * scaleFactor}px`}
                            data-height={`${(labelWidth * 3) * scaleFactor}px`}
                            style={{
                                width: `${(labelHeight * 3) * scaleFactor}px`,
                                height: `${(labelWidth * 3) * scaleFactor}px`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {/* Size Indicator - değerlerin yerlerini değiştirdik */}
                            <div className="size-indicator">
                                <div className="width-indicator">{`${labelHeight}mm`}</div>
                                <div className="height-indicator">{`${labelWidth}mm`}</div>
                                <div className="dimension-line width-line" />
                                <div className="dimension-line height-line" />
                            </div>
                            {renderLabelContent()}
                        </div>
                    </div>
                    <div className="order-section desktop-order-section">
                        <div className="quantity-selector">
                            <label htmlFor="quantity">{t("quantity", "Quantity:")}</label>
                            <select
                                id="quantity"
                                value={quantity}
                                onChange={(e) => setQuantity(Number(e.target.value))}
                                disabled={loading}
                            >
                                {activeQuantities.map(q => (
                                    <option key={q} value={q}>{q} {t("pieces", "pieces")}</option>
                                ))}
                            </select>
                        </div>
                        {isValid && (
                            <div className="price-info">
                                <div className="total-price">
                                    <span>{t("total_price", "Total Price")}</span>
                                    <div className="price-with-discount">
                                        <span>{window.careld_ajax_obj?.currency_symbol || '$'} {prices.total}</span>
                                        {prices.discount > 0 && !prices.isMarkup && (
                                            <span className="discount-info">
                                                -{prices.discount}% {t("off", "off")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="price-per-piece">
                                    {window.careld_ajax_obj?.currency_symbol || '$'} {prices.perPiece} {t("per_piece", "per piece")}
                                </div>
                            </div>
                        )}
                        <button
                            className={`add-to-cart-button ${loading ? 'loading' : ''}`}
                            onClick={addToCart}
                            disabled={loading || (!text1.trim() && !text2.trim() && !text3.trim())}
                        >
                            {loading ? t('adding_to_cart', 'Adding to Cart...') : t('add_to_cart', 'Add to Cart')}
                        </button>
                    </div>
                </div>
                {/* Fullscreen Click-to-Zoom Lightbox Modal */}
                {isPreviewZoomed && createPortal(
                    <div
                        className="preview-lightbox-overlay"
                        onClick={() => setIsPreviewZoomed(false)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: 'rgba(15, 15, 15, 0.85)',
                            backdropFilter: 'blur(8px)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 999999,
                            cursor: 'zoom-out'
                        }}
                    >
                        <div
                            className="lightbox-preview-container"
                            onClick={() => setIsPreviewZoomed(false)}
                            style={{
                                transformOrigin: 'center center',
                                display: 'flex',
                                gap: '24px',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            <div
                                className="label-preview lightbox-card-zoom"
                                style={{
                                    width: `${(labelHeight * 3) * scaleFactor}px`,
                                    height: `${(labelWidth * 3) * scaleFactor}px`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#ffffff',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
                                }}
                            >
                                {renderLabelContent()}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {/* Fullscreen Blocking Add-to-Cart Loader Spinner */}
                {loading && createPortal(
                    <div
                        className="global-blocking-loader"
                        style={{
                            position: 'fixed',
                            inset: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            backdropFilter: 'blur(4px)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 99999999,
                            pointerEvents: 'all'
                        }}
                    >
                        <div className="global-loading-spinner" style={{
                            width: '50px',
                            height: '50px',
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #2563eb',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginBottom: '16px'
                        }} />
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827', fontFamily: 'sans-serif' }}>
                            {t('adding_to_cart', 'Adding to cart...')}
                        </span>
                    </div>,
                    document.body
                )}
            </div>

            {/* Mobile Order Section - Direct child of designer-container for correct position:fixed */}
            <div className="order-section mobile-order-section">
                <div className="quantity-selector">
                    <label htmlFor="quantity-mobile">{t("quantity", "Quantity:")}</label>
                    <select
                        id="quantity-mobile"
                        value={quantity}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                        disabled={loading}
                    >
                        {activeQuantities.map(q => (
                            <option key={q} value={q}>{q} {t("pieces", "pieces")}</option>
                        ))}
                    </select>
                </div>
                {isValid && prices.discount > 0 && !prices.isMarkup && (
                    <span className="discount-badge-stuck">
                        -{prices.discount}%
                    </span>
                )}
                {isValid && (
                    <div className="price-info">
                        <div className="total-price">
                            <span>{window.careld_ajax_obj?.currency_symbol || '$'} {prices.total}</span>
                        </div>
                        <div className="price-per-piece">
                            {window.careld_ajax_obj?.currency_symbol || '$'} {prices.perPiece} {t("per_piece", "per piece")}
                        </div>
                    </div>
                )}
                <button
                    className={`add-to-cart-button ${loading ? 'loading' : ''}`}
                    onClick={addToCart}
                    disabled={loading || (!text1.trim() && !text2.trim() && !text3.trim())}
                >
                    {loading ? t('adding_to_cart', 'Adding to Cart...') : t('add_to_cart', 'Add to Cart')}
                </button>
            </div>
        </div>

    );
}